import { useEffect, useState } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Location, SearchNormal1 } from 'iconsax-react'
import Input from '../../../Common/Input/Input'
import {
  CareerLevel,
  DatePosted,
  IFindJobsFilter,
  JobType,
  WorkPlace,
} from '../../../../types/findJobsType'
import Select from '../../../Common/Select/Select'
import useDomains from '../../../OnBoarding/CreateResume/useDomains'
import Switch from '../../../Common/Switch/Switch'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import { StyledFilterBox } from '../../style'

type Props = {
  filter: IFindJobsFilter
  setFilter: React.Dispatch<React.SetStateAction<IFindJobsFilter>>
}

const jobTypes: {
  label: string
  value: JobType
}[] = [
  {
    label: 'Agency/Studio or Small Business',
    value: 'agency_small_business',
  },
  {
    label: 'Just Starting Out',
    value: 'just_starting_out',
  },
  {
    label: 'Part-Time Freelancer',
    value: 'part_time_freelancer',
  },
  {
    label: 'Full-Time Freelancer',
    value: 'full_time_freelancer',
  },
]

const workplaces: {
  label: string
  value: WorkPlace
}[] = [
  {
    label: 'In-Person',
    value: 'in_person',
  },
  {
    label: 'Hybrid',
    value: 'hybrid',
  },
  {
    label: 'Remote',
    value: 'remote',
  },
]

const dateOptions: { label: string; value: DatePosted }[] = [
  {
    label: 'Last Hour',
    value: 'last_hour',
  },
  {
    label: 'Last 24 Hours',
    value: 'last_day',
  },
  {
    label: 'Last 7 Days',
    value: 'last_week',
  },
  {
    label: 'Last 14 Days',
    value: 'last_two_week',
  },
  {
    label: 'Last 30 Days',
    value: 'last_month',
  },
  {
    label: 'All',
    value: 'all',
  },
]

const careerLevels: {
  label: string
  value: CareerLevel
}[] = [
  {
    label: 'Intern',
    value: 'intern',
  },
  {
    label: 'Junior',
    value: 'junior',
  },
  {
    label: 'Mid',
    value: 'mid',
  },
  {
    label: 'Lead',
    value: 'lead',
  },
  {
    label: 'Senior',
    value: 'senior',
  },
]

const DELAY = 700

const FilterBox: React.FC<Props> = ({ filter, setFilter }) => {
  const { data } = useDomains()
  const [searchInput, setSearchInput] = useState('')
  const [locationInput, setLocationInput] = useState('')

  useEffect(() => {
    setSearchInput(filter.search)
    setLocationInput(filter.location)
  }, [filter.search, filter.location])

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setFilter(prev => ({ ...prev, search: searchInput.trim() }))
    }, DELAY)

    return () => {
      clearTimeout(debounceTimeout)
    }
  }, [filter, searchInput, setFilter])

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setFilter(prev => ({ ...prev, location: locationInput.trim() }))
    }, DELAY)

    return () => {
      clearTimeout(debounceTimeout)
    }
  }, [filter, locationInput, setFilter])

  return (
    <StyledFilterBox spacing={5} className="default-form">
      <Stack spacing={3.75}>
        <Input
          type="text"
          placeholder="Job title, keywords or company"
          name="search"
          label="Search by Keywords"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          startAdornment={
            <SearchNormal1
              size="20"
              color="#9D9D9D"
              variant="Outline"
              className="start-adornment"
            />
          }
        />
        <Input
          type="text"
          placeholder="Country or city"
          name="search"
          label="Location"
          value={locationInput}
          onChange={e => setLocationInput(e.target.value)}
          startAdornment={
            <Location
              size="20"
              color="#9D9D9D"
              variant="Outline"
              className="start-adornment"
            />
          }
        />
        <Select
          value={filter.category}
          onChange={e =>
            setFilter({ ...filter, category: Number(e.target.value) })
          }
          required
          placeholder="Choose a Category"
          label="Category"
        >
          {data &&
            data.data.map(domain => (
              <option key={domain.id} value={domain.id}>
                {domain.label}
              </option>
            ))}
        </Select>
      </Stack>
      <Stack spacing={5}>
        <Stack>
          <Typography variant="body_m_medium" mb={1.25}>
            Job type
          </Typography>
          <Stack spacing={2}>
            {jobTypes.map(type => (
              <Switch
                key={type.value}
                label={type.label}
                switchProps={{
                  checked: filter.jobType.includes(type.value),
                  onChange: e => {
                    if (e.target.checked) {
                      setFilter({
                        ...filter,
                        jobType: [...filter.jobType, type.value],
                      })
                    } else {
                      setFilter(prev => ({
                        ...prev,
                        jobType: prev.jobType.filter(
                          jobType => jobType !== type.value
                        ),
                      }))
                    }
                  },
                }}
              />
            ))}
          </Stack>
        </Stack>
        <Stack>
          <Typography variant="body_m_medium" mb={1.25}>
            Workplace
          </Typography>
          <Stack spacing={2}>
            {workplaces.map(type => (
              <Switch
                key={type.value}
                label={type.label}
                switchProps={{
                  checked: filter.workplace.includes(type.value),
                  onChange: e => {
                    if (e.target.checked) {
                      setFilter({
                        ...filter,
                        workplace: [...filter.workplace, type.value],
                      })
                    } else {
                      setFilter(prev => ({
                        ...prev,
                        workplace: prev.workplace.filter(
                          jobType => jobType !== type.value
                        ),
                      }))
                    }
                  },
                }}
              />
            ))}
          </Stack>
        </Stack>
        <Stack>
          <Typography variant="body_m_medium" mb={1.25}>
            Career Level
          </Typography>
          <Stack spacing={2}>
            {careerLevels.map(type => (
              <Switch
                key={type.value}
                label={type.label}
                switchProps={{
                  checked: filter.career_level.includes(type.value),
                  onChange: e => {
                    if (e.target.checked) {
                      setFilter({
                        ...filter,
                        career_level: [...filter.career_level, type.value],
                      })
                    } else {
                      setFilter(prev => ({
                        ...prev,
                        career_level: prev.career_level.filter(
                          jobType => jobType !== type.value
                        ),
                      }))
                    }
                  },
                }}
              />
            ))}
          </Stack>
        </Stack>
        <Stack>
          <Typography variant="body_m_medium" mb={1.25}>
            Date Posted
          </Typography>
          <RadioGroup
            value={filter.date_posted}
            onChange={e => {
              setFilter(prev => ({
                ...prev,
                date_posted: e.target.value as DatePosted,
              }))
            }}
          >
            {dateOptions.map(item => (
              <FormControlLabel
                key={item.value}
                value={item.value}
                checked={filter.date_posted === item.value}
                control={
                  <Radio
                    sx={{
                      height: '40px',
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body_s_regular"
                    color={
                      filter.date_posted === item.value
                        ? 'typoColor.main'
                        : 'darkGray.main'
                    }
                  >
                    {item.label}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </Stack>
      </Stack>
    </StyledFilterBox>
  )
}

export default FilterBox
